interface HeroSectionProps {
  onOpenConsultation: () => void;
}

export default function HeroSection({ onOpenConsultation }: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/ee864818b45e26c1d8af28e1ec831479.jpg"
          alt="Compassionate in-home senior care Oakland IL"
          className="w-full h-full object-cover object-center"
        />
        {/* Layered overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2A44]/95 via-[#0F2A44]/75 to-[#0F2A44]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/60 via-transparent to-[#0F2A44]/20" />
      </div>

      {/* Decorative accent lines */}
      <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-gold/20 to-transparent pointer-events-none" />
      <div className="absolute top-32 left-12 w-40 h-px bg-gradient-to-r from-gold/30 to-transparent pointer-events-none hidden lg:block" />

      {/* Top badge */}
      <div className="absolute top-24 left-6 right-6 z-10 max-w-7xl mx-auto w-full">
        <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2.5 rounded-full">
          <i className="ri-shield-check-fill text-gold"></i>
          Licensed &amp; Insured · Background-Checked Caregivers · Oakland, IL
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-20 pb-32">
        <div className="max-w-2xl">
          {/* Pre-heading */}
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-10 h-px bg-gold"></span>
            <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">
              In-Home Senior Care · East-Central Illinois
            </p>
          </div>

          {/* H1 */}
          <h1 className="font-heading text-white font-bold leading-[1.05] mb-7" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
            Compassionate Senior Care{' '}
            <em className="text-gold not-italic">You Can Trust</em>
          </h1>

          {/* Subheadline */}
          <p className="text-white/75 leading-relaxed mb-9 max-w-xl" style={{ fontSize: 'clamp(1rem, 1.5vw, 1.2rem)' }}>
            Personalized in-home care plans crafted to help your loved ones live safely, comfortably, and independently — with caregivers who treat them like family.
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-4 mb-10">
            <button
              onClick={onOpenConsultation}
              className="whitespace-nowrap group bg-gold text-navy font-bold px-9 py-4 rounded-full text-base hover:brightness-110 transition-all cursor-pointer flex items-center gap-3"
            >
              Get Free Consultation
              <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
            <a
              href="tel:2173462042"
              className="whitespace-nowrap flex items-center gap-3 bg-white/12 backdrop-blur-sm text-white font-semibold px-9 py-4 rounded-full text-base hover:bg-white/22 transition-all border border-white/30"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-gold/25 rounded-full">
                <i className="ri-phone-fill text-gold text-sm"></i>
              </span>
              (217) 346-2042
            </a>
          </div>

          {/* Trust micro-bar */}
          <div className="flex flex-wrap gap-6 pt-5 border-t border-white/12">
            {[
              { icon: 'ri-home-heart-line', label: '500+ Families Served' },
              { icon: 'ri-star-fill', label: '5-Star Google Rated' },
              { icon: 'ri-time-fill', label: 'Care Within Days' },
              { icon: 'ri-shield-check-fill', label: 'Background Checked' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-2 text-white/65 text-sm">
                <i className={`${stat.icon} text-gold text-xs`}></i>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating contact card */}
      <div className="absolute bottom-12 right-8 hidden lg:block z-10">
        <div className="bg-white rounded-2xl px-6 py-6 w-72 border border-gray-100" style={{ boxShadow: '0 20px 60px rgba(15,42,68,0.25)' }}>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse"></span>
            <p className="text-navy/50 text-xs font-bold uppercase tracking-widest">Available Now</p>
          </div>
          <a href="tel:2173462042" className="font-heading text-navy text-xl font-bold hover:text-gold transition-colors flex items-center gap-3 mb-1">
            <span className="w-9 h-9 flex items-center justify-center bg-gold/15 rounded-full">
              <i className="ri-phone-fill text-gold text-base"></i>
            </span>
            (217) 346-2042
          </a>
          <p className="text-navy/40 text-xs pl-12 mb-4">Mon – Sun, 8am – 6pm</p>
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-1.5 mb-1">
              {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-gold text-sm"></i>)}
              <span className="text-navy text-sm font-bold ml-1">5.0</span>
            </div>
            <p className="text-navy/45 text-xs">Verified Google Reviews · Oakland, IL</p>
          </div>
          <button
            onClick={onOpenConsultation}
            className="whitespace-nowrap w-full mt-4 bg-navy text-white text-sm font-bold py-3 rounded-xl hover:bg-[#1a3d5c] transition-colors cursor-pointer"
          >
            Schedule Consultation
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/30 animate-bounce z-10">
        <span className="text-[10px] tracking-[0.2em] uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-lg"></i>
      </div>
    </section>
  );
}
